import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKStatusBadge, VLKButton, VLKRiskMeter, VLKEmptyState, toast,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";

export default function Notifications() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then(r => r.data),
  });

  const markAll = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
    toast.success("All notifications marked read");
  };

  const items = data?.items || [];

  return (
    <div>
      <VLKPageHeader
        title="Notifications"
        description="System, security, approval, recovery, and risk alerts."
        actions={
          <VLKButton variant="outline" size="md" onClick={markAll} disabled={items.every(i => i.read)}>
            <CheckCheck className="h-4 w-4" />Mark all read
          </VLKButton>
        }
      />

      {items.length === 0 ? (
        <VLKEmptyState icon={Bell} title="Inbox zero" description="You have no notifications." />
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <VLKCard
              key={n.notification_id}
              data-testid={IDS.notifItem(n.notification_id)}
              className={"p-4 flex items-start gap-4 " + (n.read ? "opacity-70" : "")}
            >
              <div className="pt-1"><VLKRiskMeter level={n.level} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <VLKStatusBadge status={n.kind === "security" ? "locked" : n.kind === "approval" ? "pending" : n.kind === "risk" ? "rejected" : "active"}>{n.kind}</VLKStatusBadge>
                  <span className="font-display text-sm">{n.title}</span>
                  {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-info" />}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{n.body}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 font-mono">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            </VLKCard>
          ))}
        </div>
      )}
    </div>
  );
}
